import { useState, useCallback, useRef } from "react";

// ── SigWeb global type declarations ──────────────────────────────────────────
declare global {
  interface Window {
    IsSigWebInstalled?:    () => boolean;
    GetSigWebVersion?:     () => string;
    SetTabletState?:       (state: number, ctx?: CanvasRenderingContext2D | null, refresh?: number) => number | undefined;
    SigWebEvent?:          () => void;
    ClearTablet?:          () => void;
    NumberOfTabletPoints?: () => number;
    SetDisplayXSize?:      (w: number) => void;
    SetDisplayYSize?:      (h: number) => void;
    SetImageXSize?:        (w: number) => void;
    SetImageYSize?:        (h: number) => void;
    SetImagePenWidth?:     (w: number) => void;
    GetSigImageB64?:       (mimeType: string) => string;
    SetLCDCaptureMode?:    (mode: number) => void;
    SetJustifyMode?:       (mode: number) => void;
    Reset?:                () => void;
    onSigPenUp?:           (() => void) | null;
    // ── LCD direct-write API (no SetTabletState required) ──────────────────
    LCDClear?:             () => string;
    LCDWriteString?:       (dest: number, mode: number, x: number, y: number, fnt: string, size: number, str: string) => void;
    LCDWriteStringWindow?: (dest: number, mode: number, x: number, y: number, fnt: string, xsize: number, ysize: number, str: string) => void;
    LcdRefresh?:           (mode: number, xp: number, yp: number, xs: number, ys: number) => void;
    LCDGetLCDSize?:        () => string;
  }
}

// Dimensions of the canvas that SigWeb renders ink strokes into.
export const SIGWEB_CANVAS_W = 500;
export const SIGWEB_CANVAS_H = 100;

// Physical LCD pixel dimensions for direct-write functions (LCDWriteString etc.)
// Matches T-LBK755SE and similar Topaz LCD signature pads.
export const LCD_W = 240;
export const LCD_H = 80;

// Image exported when Done is pressed (higher resolution than the display canvas).
const IMG_W = 700;
const IMG_H = 150;

export type SigWebStatus =
  | "idle"
  | "loading"
  | "unavailable"   // SigWeb not installed / not running
  | "displaying"    // pad connected, showing agreement text on LCD (no ink)
  | "capturing"     // pad active, accepting ink
  | "captured";

// ─────────────────────────────────────────────────────────────────────────────

const loadScript = (() => {
  // Only inject the script tag once, cache the promise.
  let pending: Promise<boolean> | null = null;
  return () => {
    if (pending) return pending;
    pending = new Promise<boolean>((resolve) => {
      const url = "http://localhost:47289/SigWeb/SigWebTablet.js";
      if (window.IsSigWebInstalled !== undefined) { resolve(true); return; }
      const s = document.createElement("script");
      s.src     = url;
      s.onload  = () => resolve(true);
      s.onerror = () => { pending = null; resolve(false); };
      document.head.appendChild(s);
    });
    return pending;
  };
})();

// ─────────────────────────────────────────────────────────────────────────────

const useSigWeb = () => {
  const [status, setStatus]             = useState<SigWebStatus>("idle");
  const [hasSignature, setHasSignature] = useState(false);

  const tmrRef      = useRef<number | undefined>(undefined);
  const eventTmrRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Check if SigWeb is available ──────────────────────────────────────────
  const check = useCallback(async (): Promise<boolean> => {
    setStatus("loading");
    try {
      const loaded = await loadScript();
      if (!loaded || !window.IsSigWebInstalled?.()) {
        setStatus("unavailable");
        return false;
      }
      setStatus("idle");
      return true;
    } catch {
      setStatus("unavailable");
      return false;
    }
  }, []);

  // ── Display-only mode: initialise pad and show agreement text on LCD ───────
  // LCDClear / LCDWriteString / LcdRefresh are direct HTTP calls to the SigWeb
  // service and do NOT require SetTabletState — so no canvas is needed here.
  const startDisplayMode = useCallback(async (): Promise<boolean> => {
    const available = await check();
    if (!available) return false;
    try {
      window.SetLCDCaptureMode?.(0);   // display only — no ink capture
      window.LCDClear?.();
      window.onSigPenUp = null;
      setStatus("displaying");
      setHasSignature(false);
      return true;
    } catch {
      setStatus("unavailable");
      return false;
    }
  }, [check]);

  // ── Write agreement text directly to the Topaz LCD ────────────────────────
  // Splits `text` into word-wrapped lines using the LCD pixel width, then
  // sends each line via LCDWriteString. Must be called AFTER startDisplayMode.
  const lcdWriteAgreementPage = useCallback((
    text: string,
    page: number,
    totalPages: number,
  ) => {
    if (!window.LCDClear || !window.LCDWriteString) return;

    window.LCDClear();

    const FONT       = "bold 11px Arial";
    const LINE_H     = 14;
    const PAD        = 4;
    const MAX_W      = LCD_W - PAD * 2;
    const hasPageNum = totalPages > 1;
    const maxY       = LCD_H - (hasPageNum ? LINE_H + 2 : 0) - PAD;

    // Word-wrap using an off-screen canvas for text measurement
    const tmp    = document.createElement("canvas");
    const tmpCtx = tmp.getContext("2d")!;
    tmpCtx.font  = FONT;

    const words: string[] = text.split(" ");
    let   line  = "";
    const lines: string[] = [];
    for (const word of words) {
      const test = line ? `${line} ${word}` : word;
      if (tmpCtx.measureText(test).width > MAX_W && line) {
        lines.push(line);
        line = word;
      } else {
        line = test;
      }
    }
    if (line) lines.push(line);

    let y = PAD;
    for (const lineText of lines) {
      if (y + LINE_H > maxY) break;
      window.LCDWriteString(0, 0, PAD, y, FONT, LINE_H, lineText);
      y += LINE_H;
    }

    if (hasPageNum) {
      window.LCDWriteString(
        0, 0, PAD, LCD_H - LINE_H - 2,
        "9px Arial", LINE_H,
        `Page ${page + 1} of ${totalPages}`,
      );
    }

    window.LcdRefresh?.(0, 0, 0, LCD_W, LCD_H);
  }, []);

  // ── Switch LCD from display to ink-capture mode (called after customer agrees) ─
  // Canvas mirroring is set up separately by startCapture once the canvas mounts.
  const switchToInkCapture = useCallback(() => {
    window.SetLCDCaptureMode?.(2);
    window.ClearTablet?.();
    setHasSignature(false);
    setStatus("capturing");
  }, []);

  // ── Activate the pad and start drawing ink into canvas ────────────────────
  const startCapture = useCallback(async (canvas: HTMLCanvasElement): Promise<boolean> => {
    const available = await check();
    if (!available) return false;
    try {
      const ctx = canvas.getContext("2d");

      window.SetDisplayXSize?.(SIGWEB_CANVAS_W);
      window.SetDisplayYSize?.(SIGWEB_CANVAS_H);
      window.SetImageXSize?.(SIGWEB_CANVAS_W);
      window.SetImageYSize?.(SIGWEB_CANVAS_H);
      window.SetJustifyMode?.(0);

      // Clear any existing event loop before starting a new one
      if (eventTmrRef.current !== null) {
        clearInterval(eventTmrRef.current);
        eventTmrRef.current = null;
      }
      eventTmrRef.current = setInterval(() => window.SigWebEvent?.(), 20);

      // Activate: passes canvas ctx so SigWeb mirrors ink strokes in real-time
      const tmr = window.SetTabletState?.(1, ctx, 50);
      tmrRef.current = tmr;

      window.SetLCDCaptureMode?.(2);
      window.ClearTablet?.();

      window.onSigPenUp = () => {
        const pts = window.NumberOfTabletPoints?.() ?? 0;
        setHasSignature(pts > 0);
      };

      setStatus("capturing");
      setHasSignature(false);
      return true;
    } catch {
      setStatus("unavailable");
      return false;
    }
  }, [check]);

  // ── Clear ink ─────────────────────────────────────────────────────────────
  const clearPad = useCallback((canvas?: HTMLCanvasElement | null) => {
    window.ClearTablet?.();
    setHasSignature(false);
    if (canvas) canvas.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height);
  }, []);

  // ── Export signature as data-URL ──────────────────────────────────────────
  const getImage = useCallback((): string | null => {
    if ((window.NumberOfTabletPoints?.() ?? 0) === 0) return null;
    window.SetImageXSize?.(IMG_W);
    window.SetImageYSize?.(IMG_H);
    window.SetImagePenWidth?.(2);
    const b64 = window.GetSigImageB64?.("image/png");
    return b64 ? `data:image/png;base64,${b64}` : null;
  }, []);

  // ── Stop the pad and clear the LCD ────────────────────────────────────────
  const stopCapture = useCallback(() => {
    window.onSigPenUp = null;
    if (eventTmrRef.current !== null) {
      clearInterval(eventTmrRef.current);
      eventTmrRef.current = null;
    }
    try {
      window.SetTabletState?.(0, null, tmrRef.current as unknown as number);
    } catch {
      try { window.SetTabletState?.(0); } catch {}
    }
    tmrRef.current = undefined;
    window.LCDClear?.();
    setStatus("idle");
    setHasSignature(false);
  }, []);

  return {
    status,
    hasSignature,
    check,
    startDisplayMode,
    lcdWriteAgreementPage,
    switchToInkCapture,
    startCapture,
    clearPad,
    getImage,
    stopCapture,
  };
};

export default useSigWeb;
